import type React from 'react';
import { diffLines } from 'diff';
import type { Change } from 'diff';

// Color tokens reference CSS custom properties from decision-brief.css:
//   --green (#2F7A3D), --green-bg (#E8F1EA)
//   --red   (#991B1B), --red-bg   (#F4E4E4)
// Both variables are defined in the :root block of decision-brief.css.

export interface PromptDiffViewerProps {
  oldText: string;
  newText: string;
  mode?: 'unified' | 'side-by-side';
  contextLines?: number;
}

const MONO_STYLE: React.CSSProperties = {
  fontFamily: "'Menlo', 'Consolas', 'Courier New', 'DejaVu Sans Mono', monospace",
  fontSize: 13,
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const LINE_BASE: React.CSSProperties = {
  ...MONO_STYLE,
  display: 'block',
  padding: '1px 8px 1px 10px',
};

const ADDED_STYLE: React.CSSProperties = {
  ...LINE_BASE,
  backgroundColor: 'var(--green-bg)',
  borderLeft: '3px solid var(--green)',
  color: '#1a3d20',
};

const REMOVED_STYLE: React.CSSProperties = {
  ...LINE_BASE,
  backgroundColor: 'var(--red-bg)',
  borderLeft: '3px solid var(--red)',
  color: '#5c1111',
};

const UNCHANGED_STYLE: React.CSSProperties = {
  ...LINE_BASE,
  borderLeft: '3px solid transparent',
};

const COLLAPSE_STYLE: React.CSSProperties = {
  ...LINE_BASE,
  borderLeft: '3px solid transparent',
  color: 'var(--ink-tertiary)',
  fontStyle: 'italic',
  padding: '4px 8px 4px 10px',
};

// Split a Change block into individual lines, preserving trailing newlines.
function splitLines(change: Change): string[] {
  const raw = change.value;
  const lines = raw.split('\n');
  // If the block ends with \n, the last element will be ""; drop it.
  if (lines.length > 0 && lines[lines.length - 1] === '') {
    lines.pop();
  }
  return lines;
}

interface LineItem {
  type: 'added' | 'removed' | 'unchanged';
  text: string;
}

function buildLineItems(changes: Change[]): LineItem[] {
  const items: LineItem[] = [];
  for (const c of changes) {
    const lines = splitLines(c);
    const type: LineItem['type'] = c.added ? 'added' : c.removed ? 'removed' : 'unchanged';
    for (const line of lines) {
      items.push({ type, text: line });
    }
  }
  return items;
}

// Collapse long runs of unchanged lines, keeping `ctx` lines of context around changes.
function collapseUnchanged(items: LineItem[], ctx: number): Array<LineItem | { type: 'collapse'; count: number }> {
  type Out = LineItem | { type: 'collapse'; count: number };
  const out: Out[] = [];

  // Find indices of changed lines
  const changedIdx = new Set<number>();
  items.forEach((it, i) => {
    if (it.type !== 'unchanged') {
      for (let j = Math.max(0, i - ctx); j <= Math.min(items.length - 1, i + ctx); j++) {
        changedIdx.add(j);
      }
    }
  });

  let i = 0;
  while (i < items.length) {
    if (changedIdx.has(i) || items[i].type !== 'unchanged') {
      out.push(items[i]);
      i++;
    } else {
      // Count run of unchanged lines not near a change
      let runEnd = i;
      while (runEnd < items.length && !changedIdx.has(runEnd) && items[runEnd].type === 'unchanged') {
        runEnd++;
      }
      const count = runEnd - i;
      if (count > ctx * 2) {
        out.push({ type: 'collapse', count });
      } else {
        for (let j = i; j < runEnd; j++) {
          out.push(items[j]);
        }
      }
      i = runEnd;
    }
  }
  return out;
}

function UnifiedView({ oldText, newText, contextLines }: { oldText: string; newText: string; contextLines: number }) {
  const changes = diffLines(oldText, newText);
  const items = buildLineItems(changes);
  const rows = collapseUnchanged(items, contextLines);

  if (rows.length === 0) {
    return (
      <div style={{ ...LINE_BASE, color: 'var(--ink-tertiary)', fontStyle: 'italic' }}>
        (текст не изменился)
      </div>
    );
  }

  return (
    <div>
      {rows.map((row, idx) => {
        if (row.type === 'collapse') {
          return (
            <span key={idx} style={COLLAPSE_STYLE}>
              ··· {row.count} строк без изменений ···
            </span>
          );
        }
        if (row.type === 'added') {
          return (
            <span key={idx} style={ADDED_STYLE}>+ {row.text}</span>
          );
        }
        if (row.type === 'removed') {
          return (
            <span key={idx} style={REMOVED_STYLE}>− {row.text}</span>
          );
        }
        return (
          <span key={idx} style={UNCHANGED_STYLE}>  {row.text}</span>
        );
      })}
    </div>
  );
}

function SideBySideView({ oldText, newText }: { oldText: string; newText: string }) {
  const changes = diffLines(oldText, newText);

  // Build parallel left (old) and right (new) columns
  const left: { text: string; type: 'removed' | 'unchanged' | 'empty' }[] = [];
  const right: { text: string; type: 'added' | 'unchanged' | 'empty' }[] = [];

  for (const c of changes) {
    const lines = splitLines(c);
    if (c.removed) {
      lines.forEach(l => { left.push({ text: l, type: 'removed' }); right.push({ text: '', type: 'empty' }); });
    } else if (c.added) {
      lines.forEach(l => { left.push({ text: '', type: 'empty' }); right.push({ text: l, type: 'added' }); });
    } else {
      lines.forEach(l => { left.push({ text: l, type: 'unchanged' }); right.push({ text: l, type: 'unchanged' }); });
    }
  }

  const colStyle: React.CSSProperties = { flex: 1, minWidth: 0, overflow: 'auto' };

  return (
    <div style={{ display: 'flex', gap: 1 }}>
      <div style={colStyle}>
        {left.map((l, i) => {
          const s = l.type === 'removed' ? REMOVED_STYLE : l.type === 'empty'
            ? { ...UNCHANGED_STYLE, opacity: 0.25 } : UNCHANGED_STYLE;
          return <span key={i} style={s}>{l.text || ' '}</span>;
        })}
      </div>
      <div style={{ width: 1, background: 'var(--border)', flexShrink: 0 }} />
      <div style={colStyle}>
        {right.map((r, i) => {
          const s = r.type === 'added' ? ADDED_STYLE : r.type === 'empty'
            ? { ...UNCHANGED_STYLE, opacity: 0.25 } : UNCHANGED_STYLE;
          return <span key={i} style={s}>{r.text || ' '}</span>;
        })}
      </div>
    </div>
  );
}

export function PromptDiffViewer({
  oldText,
  newText,
  mode = 'unified',
  contextLines = 3,
}: PromptDiffViewerProps) {
  const containerStyle: React.CSSProperties = {
    border: '1px solid var(--border)',
    borderRadius: 6,
    overflow: 'hidden',
    background: 'var(--bg-app)',
  };

  return (
    <div style={containerStyle} role="region" aria-label="Diff промпта">
      {mode === 'unified'
        ? <UnifiedView oldText={oldText} newText={newText} contextLines={contextLines} />
        : <SideBySideView oldText={oldText} newText={newText} />
      }
    </div>
  );
}
